import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, LogOut, Plus, Loader2, Power, BadgeDollarSign, Building2, TrendingUp, UserMinus, Trash2, ShieldCheck, Megaphone, LineChart, Send, ScrollText, RefreshCw, Search, Download, Gift, KeyRound, Copy, Inbox } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/download";
import { AUDIT_GROUPS as WORKSPACE_GROUPS, AuditEventPill } from "@/components/AuditTrailCard";
import { SecurityAlerts } from "@/components/admin/SecurityAlerts";
import { ContactEnquiries } from "@/components/admin/ContactEnquiries";

const AUDIT_GROUPS = [...WORKSPACE_GROUPS, ["admin", "Admin"]];

function AuditLog() {
  const [rows, setRows] = useState(null);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const load = (g = group, query = q) =>
    api.get("/admin/audit-log", { params: { event: g, q: query } })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the audit log"));
  useEffect(() => { load("", ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = async () => {
    setExporting(true);
    try { await downloadCsv("/admin/audit-log.csv", { event: group, q }, "tenx-audit-log.csv"); toast.success("Audit log exported"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };
  return (
    <Card className="p-5" data-testid="admin-audit-log">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="ff-cabinet text-lg font-bold text-slate-900">Audit log</h2>
          <p className="text-sm text-slate-500">Append-only trail of sign-ins, plan changes, shares and admin actions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => load()} data-testid="audit-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" disabled={exporting || !rows?.length} onClick={exportCsv} data-testid="audit-export-csv">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
          </Button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {AUDIT_GROUPS.map(([g, label]) => (
          <button key={label} onClick={() => { setGroup(g); load(g); }} data-testid={`audit-filter-${label.toLowerCase()}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${group === g ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actor or detail…" className="h-8 w-56" data-testid="audit-search" />
          <Button type="submit" variant="outline" size="sm" className="h-8 gap-1.5" data-testid="audit-search-btn"><Search className="h-3.5 w-3.5" /></Button>
        </form>
      </div>
      {rows === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-slate-500" data-testid="audit-empty">No audit entries match.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-4">When</th><th className="py-2 pr-4">Event</th><th className="py-2 pr-4">Actor</th><th className="py-2">Detail</th>
            </tr></thead>
            <tbody data-testid="audit-rows">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100">
                  <td className="whitespace-nowrap py-2 pr-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="py-2 pr-4"><AuditEventPill event={r.event} /></td>
                  <td className="max-w-48 truncate py-2 pr-4 text-slate-700">{r.actor || "-"}</td>
                  <td className="max-w-md truncate py-2 text-slate-600">{r.detail || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const EMPTY = { name: "", code: "", type: "percent", value: "", duration: "once", duration_in_months: "", max_redemptions: "", expires: "" };

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load coupons"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.value) return toast.error("Enter a discount value");
    const payload = {
      name: form.name || undefined, code: form.code || undefined,
      duration: form.duration,
      duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) || 1 : undefined,
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
      expires_at: form.expires ? Math.floor(new Date(form.expires).getTime() / 1000) : undefined,
    };
    if (form.type === "percent") payload.percent_off = Number(form.value);
    else payload.amount_off = Number(form.value);
    setSaving(true);
    try {
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created");
      setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create coupon"); }
    finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/admin/coupons/${c.promo_id}?active=${!c.active}`); load(); }
    catch { toast.error("Could not update coupon"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <Card className="h-fit rounded-xl border p-5" data-testid="coupon-form">
        <h3 className="ff-cabinet mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><Plus className="h-4 w-4 text-brand-ink" /> New coupon</h3>
        <div className="space-y-3">
          <div><Label className="text-xs">Internal name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Launch promo" className="mt-1" data-testid="coupon-name" /></div>
          <div><Label className="text-xs">Code (optional, auto if blank)</Label><Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="LAUNCH20" className="mt-1 uppercase" data-testid="coupon-code" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="coupon-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">% off</SelectItem><SelectItem value="amount">$ off</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{form.type === "percent" ? "Percent" : "Amount (USD)"}</Label><Input type="number" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder={form.type === "percent" ? "20" : "10"} className="mt-1" data-testid="coupon-value" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Duration</Label>
              <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
                <SelectTrigger className="mt-1" data-testid="coupon-duration"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="once">Once</SelectItem><SelectItem value="repeating">Repeating</SelectItem><SelectItem value="forever">Forever</SelectItem></SelectContent>
              </Select>
            </div>
            {form.duration === "repeating" && <div><Label className="text-xs">Months</Label><Input type="number" value={form.duration_in_months} onChange={(e) => set("duration_in_months", e.target.value)} placeholder="3" className="mt-1" data-testid="coupon-months" /></div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Max redemptions</Label><Input type="number" value={form.max_redemptions} onChange={(e) => set("max_redemptions", e.target.value)} placeholder="100" className="mt-1" data-testid="coupon-max" /></div>
            <div><Label className="text-xs">Expires</Label><DatePicker value={form.expires} onChange={(v) => set("expires", v)} placeholder="No expiry" className="mt-1 h-9" data-testid="coupon-expires" /></div>
          </div>
          <Button onClick={create} disabled={saving} className="w-full gap-1.5 font-semibold" data-testid="create-coupon-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create coupon
          </Button>
        </div>
      </Card>

      <Card className="rounded-xl border p-0" data-testid="coupon-list">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Coupons ({coupons.length})</h3></div>
        {coupons.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No coupons yet. Create one on the left.</p>
        ) : (
          <div className="divide-y">
            {coupons.map((c) => (
              <div key={c.promo_id} className="flex items-center justify-between gap-4 p-4" data-testid={`coupon-row-${c.code}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="ff-cabinet rounded bg-slate-100 px-2 py-0.5 font-bold text-slate-900">{c.code}</span>
                    <span className="text-sm font-semibold text-brand-ink">{c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `$${c.amount_off} off` : ""}</span>
                    {!c.active && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">inactive</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {c.duration}{c.duration_in_months ? ` · ${c.duration_in_months}mo` : ""} · redeemed {c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ""}
                    {c.expires_at ? ` · expires ${new Date(c.expires_at * 1000).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className={`gap-1.5 ${c.active ? "text-red-600" : "text-emerald-600"}`} onClick={() => toggle(c)} data-testid={`toggle-coupon-${c.code}`}>
                  <Power className="h-4 w-4" /> {c.active ? "Disable" : "Enable"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const REASON_LABELS = {
  too_expensive: "Too expensive", missing_features: "Missing features", switching: "Switching tools",
  not_raising: "Not fundraising", closing: "Winding down", other: "Other", unspecified: "No reason given",
};

function Churn() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/churn").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load churn data")); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const reasonRows = Object.entries(data.reasons || {}).sort((a, b) => b[1] - a[1]);
  const maxCount = Math.max(1, ...reasonRows.map(([, c]) => c));
  const topReason = reasonRows[0] ? (REASON_LABELS[reasonRows[0][0]] || reasonRows[0][0]) : "-";
  return (
    <div className="space-y-6" data-testid="churn-dashboard">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {[["Cancellation feedback", data.total_feedback], ["Canceling right now", data.canceling_now], ["Top reason", topReason]].map(([l, v]) => (
          <Card key={l} className="rounded-xl border p-5" data-testid={`churn-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="ff-cabinet text-2xl font-black text-slate-900 sm:text-3xl">{v}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</div>
          </Card>
        ))}
      </div>
      <Card className="rounded-xl border p-0" data-testid="churn-reasons">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Why founders cancel</h3></div>
        {reasonRows.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No cancellation feedback yet.</p> : (
          <div className="space-y-3 p-4">
            {reasonRows.map(([r, count]) => (
              <div key={r} data-testid={`churn-reason-${r}`}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{REASON_LABELS[r] || r}</span>
                  <span className="tabular-nums text-slate-500">{count} · {Math.round((count / Math.max(1, data.total_feedback)) * 100)}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-red-500" style={{ width: `${(count / maxCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {(data.monthly || []).length > 0 && (
        <Card className="rounded-xl border p-0" data-testid="churn-monthly">
          <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Monthly trend</h3></div>
          <div className="space-y-2 p-4">
            {data.monthly.slice(-12).map((m) => (
              <div key={m.month} className="flex items-center gap-3" data-testid={`churn-month-${m.month}`}>
                <span className="w-16 shrink-0 text-xs font-semibold text-slate-500">{m.month}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-slate-900" style={{ width: `${(m.count / Math.max(1, ...data.monthly.map((x) => x.count))) * 100}%` }} />
                </div>
                <span className="w-8 shrink-0 text-right text-xs tabular-nums text-slate-600">{m.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card className="rounded-xl border p-0" data-testid="churn-recent">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Recent feedback</h3></div>
        {data.recent.length === 0 ? <p className="p-6 text-sm text-muted-foreground">Nothing yet.</p> : (
          <div className="divide-y">
            {data.recent.map((f) => (
              <div key={f.id} className="p-4" data-testid={`churn-row-${f.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-slate-900">{f.company}</span>
                  {f.plan && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">{f.plan}</span>}
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase text-red-600">{REASON_LABELS[f.reason] || f.reason}</span>
                  <span className="ml-auto text-xs text-slate-400">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</span>
                </div>
                {f.comment && <p className="mt-1.5 text-sm text-slate-600">“{f.comment}”</p>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Revenue() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then(({ data }) => setData(data)).catch(() => toast.error("Could not load revenue")); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const maxMix = Math.max(1, ...data.plan_mix.map((p) => p.count));
  const PLAN_LABEL = { starter: "Starter", growth: "Growth", scale: "Scale", fund_manager: "Fund Manager" };
  return (
    <div className="space-y-6" data-testid="revenue-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["MRR", `$${data.mrr.toLocaleString()}`], ["ARR", `$${data.arr.toLocaleString()}`], ["Paying companies", data.paying], ["Collected (all time)", `$${data.total_collected.toLocaleString()}`]].map(([k, v]) => (
          <Card key={k} className="rounded-xl border p-4" data-testid={`revenue-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</div>
            <div className="h-display mt-1 text-2xl font-bold text-slate-900">{v}</div>
          </Card>
        ))}
      </div>
      <Card className="rounded-xl border p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Revenue collected by month</h3>
            <p className="text-xs text-slate-500">{data.paid_transactions} paid transactions</p>
          </div>
        </div>
        {data.monthly_revenue.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">No paid transactions yet.</p>
        ) : (
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_revenue} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} width={54} />
                <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "Collected"]} cursor={{ fill: "hsl(var(--foreground) / 0.05)" }} />
                <Bar dataKey="amount" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
      <Card className="rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900">Plan mix</h3>
        <p className="text-xs text-slate-500">{data.total_companies} companies total</p>
        <div className="mt-4 space-y-2.5">
          {data.plan_mix.map((p) => (
            <div key={p.plan} className="flex items-center gap-3" data-testid={`plan-mix-${p.plan}`}>
              <span className="w-28 shrink-0 text-sm font-medium text-slate-700">{PLAN_LABEL[p.plan] || p.plan}</span>
              <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
                    <div className={`h-full rounded ${p.plan === "starter" ? "bg-slate-300" : p.plan === "fund_manager" ? "bg-brand" : "bg-primary"}`} style={{ width: `${(p.count / maxMix) * 100}%` }} />
              </div>
              <span className="w-8 shrink-0 text-right text-sm font-bold text-slate-900">{p.count}</span>
            </div>
          ))}
        </div>
      </Card>
      <WeeklyDigest />
    </div>
  );
}

function WeeklyDigest() {
  const [info, setInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const load = () => api.get("/admin/digest").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.sent ? `Digest sent to ${data.to}` : "Digest could not be sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send digest"); }
    finally { setSending(false); }
  };
  const CHIPS = info ? [
    ["New founders", info.stats.new_founders], ["New investors", info.stats.new_investors],
    ["Revenue this week", `$${Number(info.stats.revenue_week).toLocaleString()}`],
    ["Deck visits", info.stats.deck_visits], ["Interested", info.stats.deck_interested_week ?? 0],
    ["Going dark", info.stats.ghosting_prospects ?? 0], ["Updates published", info.stats.updates_published],
  ] : [];
  return (
    <Card className="rounded-xl border p-5" data-testid="weekly-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">Weekly digest</h3>
          <p className="text-xs text-slate-500">
            Auto-emailed to {info?.recipient || "the admin"} every Monday. Last sent:{" "}
            {info?.last_sent ? `${new Date(info.last_sent.sent_at).toLocaleString()} (${info.last_sent.manual ? "manual" : "auto"})` : "never"}
          </p>
        </div>
        <Button onClick={send} disabled={sending || !info?.email_configured} className="gap-1.5" data-testid="digest-send-btn">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
        </Button>
      </div>
      {info && (
        <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {CHIPS.map(([k, v]) => (
            <div key={k} className="rounded-lg border bg-secondary/40 px-3 py-2" data-testid={`digest-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{k}</div>
              <div className="h-display text-lg font-bold text-slate-900">{v}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Announce() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ subject: "", body: "", audience: "all" });
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/announce").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (testOnly) => {
    if (!form.subject.trim() || !form.body.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(testOnly ? "test" : "real");
    try {
      const { data } = await api.post("/admin/announce", { ...form, test_only: testOnly });
      toast.success(testOnly ? "Test email sent to you" : `Announcement queued to ${data.queued} users`);
      if (!testOnly) setForm({ subject: "", body: "", audience: "all" });
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(null); }
  };
  const count = info?.counts?.[form.audience] ?? 0;
  return (
    <div className="space-y-6" data-testid="announce-tab">
      <Card className="rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900">Send an announcement</h3>
        <p className="text-xs text-slate-500">One-off product email, wrapped in the TenX template. Always send yourself a test first.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_200px]">
          <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="What's new in TenX this month" className="mt-1" data-testid="announce-subject" /></div>
          <div>
            <Label className="text-xs">Audience</Label>
            <Select value={form.audience} onValueChange={(v) => setForm((f) => ({ ...f, audience: v }))}>
              <SelectTrigger className="mt-1" data-testid="announce-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone ({info?.counts?.all ?? "…"})</SelectItem>
                <SelectItem value="founders">Founders ({info?.counts?.founders ?? "…"})</SelectItem>
                <SelectItem value="investors">Investors ({info?.counts?.investors ?? "…"})</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3"><Label className="text-xs">Message (blank line = new paragraph)</Label><Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={7} placeholder="Hi there,&#10;&#10;We just launched..." className="mt-1" data-testid="announce-body" /></div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => send(true)} disabled={!!busy} className="gap-1.5" data-testid="announce-test-btn">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test to me
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
          <Button disabled={!!busy} className="gap-1.5" data-testid="announce-send-btn">
                {busy === "real" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send to {count} user{count === 1 ? "" : "s"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Email {count} real user{count === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>"{form.subject || "(no subject)"}" will be sent to {form.audience === "all" ? "everyone" : form.audience} immediately. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="announce-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => send(false)} data-testid="announce-confirm">Send it</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
      <Card className="rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900">History</h3>
        <div className="mt-3 divide-y">
          {(info?.history || []).length === 0 && <p className="py-6 text-center text-sm text-slate-400">No announcements sent yet.</p>}
          {(info?.history || []).map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`announce-row-${a.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800">{a.subject}{a.test && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">test</span>}</div>
                <div className="text-xs text-slate-400">{(a.created_at || "").slice(0, 16).replace("T", " ")} · {a.audience} · {a.sent_count}/{a.recipient_count} sent · {a.status}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function CouponAssignments() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", emails: "", note: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/coupon-assignments").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const assign = async () => {
    if (!form.code.trim() || !form.emails.trim()) { toast.error("Coupon code and at least one email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/coupon-assignments", { code: form.code, emails: form.emails, note: form.note });
      toast.success(`${form.code.toUpperCase()} assigned to ${data.created} user${data.created === 1 ? "" : "s"}${data.skipped?.length ? ` (${data.skipped.length} already assigned)` : ""}`);
      setForm({ code: "", emails: "", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assignment failed"); }
    finally { setSaving(false); }
  };
  const remove = async (r) => {
    try { await api.delete(`/admin/coupon-assignments/${r.id}`, { params: { code: r.code, email: r.email } }); toast.success("Assignment removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="rounded-xl border p-5" data-testid="coupon-assignments-card">
      <h3 className="ff-cabinet text-lg font-bold text-slate-900">User-specific coupons</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Assign an existing coupon code to a user's email. It's applied automatically at their next checkout and reserved - nobody else can redeem it.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Coupon code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="FOUNDER50" className="mt-1 h-9 w-40 uppercase" data-testid="assign-code-input" /></div>
        <div className="min-w-56 flex-1"><Label className="text-xs">User email(s)</Label><Textarea value={form.emails} onChange={(e) => setForm((f) => ({ ...f, emails: e.target.value }))} placeholder="one email per line, or comma-separated for a launch blast" className="mt-1 min-h-9 py-2 text-sm" rows={1} data-testid="assign-email-input" /></div>
        <div className="min-w-40 flex-1"><Label className="text-xs">Note (optional)</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="YC batch discount" className="mt-1 h-9" data-testid="assign-note-input" /></div>
        <Button onClick={assign} disabled={saving} className="gap-2" data-testid="assign-coupon-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Assign</Button>
      </div>
      <div className="mt-4 space-y-2">
        {rows.length === 0 && <p className="text-xs text-muted-foreground" data-testid="assignments-empty">No user-specific coupons yet.</p>}
        {rows.map((r, index) => (
          <div key={`${r.id}:${r.code}:${r.email}:${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm" data-testid={`assignment-row-${r.id}-${index}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-xs font-bold text-white">{r.code}</span>
              <span className="font-medium text-slate-900">{r.email}</span>
              {r.user_name && <span className="text-xs text-muted-foreground">({r.user_name})</span>}
              {r.note && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{r.note}</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(r)} data-testid={`assignment-delete-${r.id}-${index}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Cleanup() {
  const [data, setData] = useState(null);
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/test-companies").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load test companies"));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setPurging(true);
    try {
      const { data: r } = await api.post("/admin/test-companies/purge", { company_ids: (data?.companies || []).map((c) => c.id) });
      setResult(r);
      toast.success(`Purged ${r.companies_deleted} companies, ${r.users_deleted} users and ${r.rows_deleted} data rows`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    finally { setPurging(false); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="cleanup-panel">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {[["Test companies found", data.total], ["Linked test users", data.user_count], ["Last purge", result ? `${result.companies_deleted} removed` : "-"]].map(([l, v]) => (
          <Card key={l} className="rounded-xl border p-5" data-testid={`cleanup-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="ff-cabinet text-2xl font-black text-slate-900 sm:text-3xl">{v}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</div>
          </Card>
        ))}
      </div>
      <Card className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center gap-2 text-sm text-emerald-900">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          Protected and never touched: Northwind Labs, Gate Co, and any company with a Stripe subscription or paid transaction.
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="gap-1.5" disabled={purging || data.total === 0} data-testid="cleanup-purge-btn">
              {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Purge {data.total} test compan{data.total === 1 ? "y" : "ies"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="cleanup-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Permanently delete {data.total} test compan{data.total === 1 ? "y" : "ies"}?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the companies listed below, their {data.user_count} user account{data.user_count === 1 ? "" : "s"} and every related row (metrics, updates, investors, documents, links, prospects…). This cannot be undone. Demo and paying companies are excluded automatically.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cleanup-cancel-btn">Keep them</AlertDialogCancel>
              <AlertDialogAction onClick={purge} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="cleanup-confirm-purge">Purge everything</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
      <Card className="rounded-xl border p-0" data-testid="cleanup-companies-list">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Matched test companies ({data.total})</h3></div>
        {data.companies.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Nothing to clean - the database has no leftover test companies.</p>
        ) : (
          <div className="max-h-[420px] divide-y overflow-y-auto">
            {data.companies.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-4 px-4 py-2.5" data-testid={`cleanup-row-${c.id}`}>
                <span className="text-sm font-medium text-slate-900">{c.name}</span>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">{c.plan}</span>
                  <span className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function CouponAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/coupons/analytics").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load analytics")); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="coupon-analytics">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[["Coupons", data.totals.coupons], ["Active coupons", data.totals.active_coupons], ["Redemptions", data.totals.redemptions], ["Active subs via coupons", data.totals.active_subscriptions]].map(([l, v]) => (
          <Card key={l} className="rounded-xl border p-5" data-testid={`analytics-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="ff-cabinet text-3xl font-black text-slate-900">{v}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</div>
          </Card>
        ))}
      </div>
      <Card className="rounded-xl border p-0" data-testid="analytics-table">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Performance by code</h3></div>
        {data.coupons.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No coupons yet.</p>
        ) : (
          <div className="divide-y">
            <div className="grid grid-cols-5 gap-2 p-3 text-[10px] font-bold uppercase tracking-wide text-slate-400">
              <span>Code</span><span>Discount</span><span className="text-right">Redeemed</span><span className="text-right">Active subs</span><span className="text-right">Conversion</span>
            </div>
            {data.coupons.map((c) => (
              <div key={c.promo_id} className="grid grid-cols-5 items-center gap-2 p-3 text-sm" data-testid={`analytics-row-${c.code}`}>
                <span className="ff-cabinet flex items-center gap-2 font-bold text-slate-900">{c.code}{!c.active && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">inactive</span>}</span>
                          <span className="text-brand-ink">{c.percent_off ? `${c.percent_off}%` : c.amount_off ? `$${c.amount_off}` : ""}</span>
                <span className="text-right tabular-nums">{c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ""}</span>
                <span className="text-right tabular-nums">{c.active_subscriptions}</span>
                <span className="text-right tabular-nums text-slate-500">{c.conversion_pct != null ? `${c.conversion_pct}%` : "-"}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Subscriptions() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load")); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {[["Companies", data.totals.companies, Building2], ["Paid plans (incl. comped)", data.totals.paying, BadgeDollarSign], ["Paid transactions", data.transactions.filter((t) => t.payment_status === "paid").length, Ticket]].map(([l, v, Icon]) => (
          <Card key={l} className="rounded-xl border p-5" data-testid={`stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <Icon className="h-5 w-5 text-slate-400" />
            <div className="ff-cabinet mt-3 text-3xl font-black text-slate-900">{v}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</div>
          </Card>
        ))}
      </div>
      <Card className="rounded-xl border p-0" data-testid="subscriptions-table">
        <div className="border-b p-4"><h3 className="ff-cabinet text-lg font-bold text-slate-900">Companies</h3></div>
        <div className="divide-y">
          {data.companies.map((c) => (
            <div key={c.company_id} className="flex items-center justify-between gap-4 p-4">
              <span className="font-medium text-slate-900">{c.company_name}</span>
              <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${c.plan === "starter" ? "bg-slate-100 text-slate-600" : "bg-brand/10 text-brand-ink"}`}>{c.plan}</span>
                <span className="text-xs text-muted-foreground">{c.paid_transactions} paid</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const COMP_PLAN_OPTIONS = [["scale", "Scale"], ["growth", "Growth"], ["fund_manager", "Fund Manager"]];

const CODE_STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700",
  redeemed: "bg-slate-200 text-slate-600",
  revoked: "bg-red-100 text-red-700",
};

function AccessCodes() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ count: 20, plan: "scale", label: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/access-codes").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    const n = Number(form.count) || 0;
    if (n < 1 || n > 100) { toast.error("Generate between 1 and 100 codes"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/access-codes/generate", { count: n, plan: form.plan, label: form.label });
      toast.success(`Generated ${data.created} ${data.plan_name} code${data.created === 1 ? "" : "s"}`);
      setForm((f) => ({ ...f, label: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate codes"); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => { try { await api.post(`/admin/access-codes/${id}/revoke`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const del = async (id) => { try { await api.delete(`/admin/access-codes/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(label); };

  const codes = data?.codes || [];
  const stats = data?.stats || { total: 0, active: 0, redeemed: 0, revoked: 0 };
  const activeCodes = codes.filter((c) => c.status === "active");
  const link = (c) => `${window.location.origin}/redeem/${c.code}`;

  return (
    <Card className="rounded-xl border p-5" data-testid="access-codes-card">
      <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-brand-ink" />
        <h3 className="ff-cabinet text-lg font-bold text-slate-900">Free-access codes</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Generate single-use codes that grant a founder a paid plan for free, forever. Share the code or the redeem link - each works once.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">How many</Label><Input type="number" min={1} max={100} value={form.count} onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))} className="mt-1 h-9 w-24" data-testid="access-count" /></div>
        <div>
          <Label className="text-xs">Plan</Label>
          <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
            <SelectTrigger className="mt-1 h-9 w-40" data-testid="access-plan"><SelectValue /></SelectTrigger>
            <SelectContent>{COMP_PLAN_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-40 flex-1"><Label className="text-xs">Batch label (optional)</Label><Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Founder friends, YC batch…" className="mt-1 h-9" data-testid="access-label" /></div>
        <Button onClick={generate} disabled={busy} className="h-9 gap-2" data-testid="access-generate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate</Button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600" data-testid="access-stat-total">{stats.total} total</span>
        <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-medium text-emerald-700" data-testid="access-stat-active">{stats.active} available</span>
        <span className="rounded-full bg-slate-200 px-2.5 py-1 font-medium text-slate-600" data-testid="access-stat-redeemed">{stats.redeemed} redeemed</span>
        {stats.revoked > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-700">{stats.revoked} revoked</span>}
        {activeCodes.length > 0 && (
          <>
            <Button size="sm" variant="outline" className="ml-auto h-7 gap-1.5 text-xs" onClick={() => copy(activeCodes.map((c) => c.code).join("\n"), `Copied ${activeCodes.length} codes`)} data-testid="access-copy-all-codes"><Copy className="h-3.5 w-3.5" /> Copy all codes</Button>
            <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => copy(activeCodes.map((c) => link(c)).join("\n"), `Copied ${activeCodes.length} links`)} data-testid="access-copy-all-links"><Copy className="h-3.5 w-3.5" /> Copy all links</Button>
          </>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {codes.length === 0 && <p className="text-xs text-muted-foreground" data-testid="access-codes-empty">No access codes yet. Generate a batch above.</p>}
        {codes.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm" data-testid={`access-code-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-xs font-bold text-white">{c.code}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${CODE_STATUS_BADGE[c.status]}`}>{c.status}</span>
              <span className="text-xs font-medium text-slate-500">{c.plan_name}</span>
              {c.label && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{c.label}</span>}
              {c.status === "redeemed" && <span className="text-xs text-slate-500">→ {c.redeemed_company_name || c.redeemed_by_email}</span>}
            </div>
            <div className="flex items-center gap-1">
              {c.status === "active" && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => copy(c.code, "Code copied")} data-testid={`access-copy-code-${c.id}`}><Copy className="h-3.5 w-3.5" /> Code</Button>
                  <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => copy(link(c), "Link copied")} data-testid={`access-copy-link-${c.id}`}><Copy className="h-3.5 w-3.5" /> Link</Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => del(c.id)} title="Delete" data-testid={`access-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DirectComps() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", plan: "scale", note: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/comps").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const grant = async () => {
    if (!form.email.trim()) { toast.error("Enter a founder email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/comp", { email: form.email.trim(), plan: form.plan, note: form.note });
      toast.success(`${data.company_name} is now on ${data.plan_name} - free`);
      setForm({ email: "", plan: "scale", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant access"); }
    finally { setBusy(false); }
  };
  const revoke = async (company_id) => { try { await api.post("/admin/comp/revoke", { company_id }); toast.success("Complimentary access revoked"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const comps = data?.comps || [];
  return (
    <Card className="rounded-xl border p-5" data-testid="direct-comps-card">
      <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-brand-ink" />
        <h3 className="ff-cabinet text-lg font-bold text-slate-900">Comp a founder directly</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Instantly give free access to a founder who already has an account - no code needed. They must be a founder workspace owner.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-56 flex-1"><Label className="text-xs">Founder email</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="founder@company.com" className="mt-1 h-9" data-testid="comp-email" /></div>
        <div>
          <Label className="text-xs">Plan</Label>
          <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
            <SelectTrigger className="mt-1 h-9 w-40" data-testid="comp-plan"><SelectValue /></SelectTrigger>
            <SelectContent>{COMP_PLAN_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-40 flex-1"><Label className="text-xs">Note (optional)</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Reason" className="mt-1 h-9" data-testid="comp-note" /></div>
        <Button onClick={grant} disabled={busy} className="h-9 gap-2" data-testid="comp-grant-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Grant access</Button>
      </div>

      <div className="mt-5">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Companies on complimentary access ({comps.length})</div>
        <div className="space-y-2">
          {comps.length === 0 && <p className="text-xs text-muted-foreground" data-testid="comps-empty">No comped companies yet.</p>}
          {comps.map((c) => (
            <div key={c.company_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm" data-testid={`comp-row-${c.company_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-900">{c.company_name}</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">{c.plan_name}</span>
                {c.founder_email && <span className="text-xs text-slate-500">{c.founder_email}</span>}
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{c.source === "access_code" ? `code ${c.code || ""}` : "direct"}</span>
                {c.note && <span className="text-xs text-slate-400">{c.note}</span>}
              </div>
              <Button size="sm" variant="ghost" className="h-7 gap-1.5 px-2 text-xs text-red-600" onClick={() => revoke(c.company_id)} data-testid={`comp-revoke-${c.company_id}`}><Power className="h-3.5 w-3.5" /> Revoke</Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header data-testid="admin-header" className="border-b border-white/15 bg-sidebar text-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <Logo compact light />
            <span data-testid="admin-role-badge" className="rounded bg-brand/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="hidden text-sm text-white/65 sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" className="gap-1.5 text-white hover:bg-white/10 hover:text-white" onClick={doLogout} data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-8">
        <h1 className="ff-cabinet text-3xl font-bold tracking-tight text-slate-900">tenx control center</h1>
        <p className="ff-manrope mt-1 text-slate-500">Generate discount coupons and monitor subscriptions.</p>
        <Tabs defaultValue="coupons" className="mt-8">
          <TabsList className="h-auto flex-wrap justify-start" data-testid="admin-tabs">
            <TabsTrigger value="coupons" data-testid="tab-coupons"><Ticket className="mr-1.5 h-4 w-4" /> Coupons</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access"><KeyRound className="mr-1.5 h-4 w-4" /> Free access</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics"><TrendingUp className="mr-1.5 h-4 w-4" /> Analytics</TabsTrigger>
            <TabsTrigger value="subs" data-testid="tab-subscriptions"><BadgeDollarSign className="mr-1.5 h-4 w-4" /> Subscriptions</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue"><LineChart className="mr-1.5 h-4 w-4" /> Revenue</TabsTrigger>
            <TabsTrigger value="announce" data-testid="tab-announce"><Megaphone className="mr-1.5 h-4 w-4" /> Announce</TabsTrigger>
            <TabsTrigger value="churn" data-testid="tab-churn"><UserMinus className="mr-1.5 h-4 w-4" /> Churn</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="mr-1.5 h-4 w-4" /> Audit</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck className="mr-1.5 h-4 w-4" /> Security</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Inbox className="mr-1.5 h-4 w-4" /> Enquiries</TabsTrigger>
            <TabsTrigger value="cleanup" data-testid="tab-cleanup"><Trash2 className="mr-1.5 h-4 w-4" /> Cleanup</TabsTrigger>
          </TabsList>
          <TabsContent value="coupons" className="mt-6"><div className="space-y-6"><Coupons /><CouponAssignments /></div></TabsContent>
          <TabsContent value="access" className="mt-6"><div className="space-y-6"><AccessCodes /><DirectComps /></div></TabsContent>
          <TabsContent value="analytics" className="mt-6"><CouponAnalytics /></TabsContent>
          <TabsContent value="subs" className="mt-6"><Subscriptions /></TabsContent>
          <TabsContent value="revenue" className="mt-6"><Revenue /></TabsContent>
          <TabsContent value="announce" className="mt-6"><Announce /></TabsContent>
          <TabsContent value="churn" className="mt-6"><Churn /></TabsContent>
          <TabsContent value="audit" className="mt-6"><AuditLog /></TabsContent>
          <TabsContent value="security" className="mt-6"><SecurityAlerts /></TabsContent>
          <TabsContent value="enquiries" className="mt-6"><ContactEnquiries /></TabsContent>
          <TabsContent value="cleanup" className="mt-6"><Cleanup /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
